/**
 * header-scroll.js — Benigno
 *
 * Controla o estado visual do header:
 *   is-transparent  → header sobre um banner (topo da página)
 *   is-solid        → header fora de qualquer banner (rolou / página sem banner)
 *
 * Também mantém --header-height atualizado para posicionar o mega menu dropdown.
 */
(function () {
  'use strict';

  const BANNER_SELECTORS = [
    '[class*="benigno-hero"]',
    '[class*="hero-banner"]',
    '.shopify-section:has(.collection-hero--with-image)',
    '.shopify-section:has(.simple-banner__slides)',
    '.shopify-section:has([id^="simple-banner-"])',
    '.shopify-section:has(.bh-viewport)',
    '.shopify-section:has(.video-banner)',
    '.shopify-section:has(.slideshow)',
    '.shopify-section:has(slideshow-component)',
    '.shopify-section:has(.image-banner)',
    '.shopify-section:has(.banner)',
  ];

  const CLASS_TRANSPARENT = 'is-transparent';
  const CLASS_SOLID       = 'is-solid';

  function getSectionHeader() {
    return document.querySelector('.section-header');
  }

  function findFirstBanner() {
    for (const selector of BANNER_SELECTORS) {
      try {
        const el = document.querySelector(selector);
        if (el) return el;
      } catch (_) {}
    }
    return null;
  }

  function setTransparent(header) {
    header.classList.add(CLASS_TRANSPARENT);
    header.classList.remove(CLASS_SOLID);
  }

  function setSolid(header) {
    header.classList.remove(CLASS_TRANSPARENT);
    header.classList.add(CLASS_SOLID);
  }

  /**
   * Mantém --announcement-visible-height = quanto da faixa de avisos (.ann-bar)
   * ainda está visível conforme a rolagem.
   *
   * IMPORTANTE: isto NÃO posiciona mais o header — o encaixe é sticky nativo.
   * A var só ancora painéis `position: fixed` que "descem" do header: o dropdown
   * da conta (component-login-drawer.css) e o cart drawer (cart-drawer-benigno.css).
   * Sem ela, no topo da página esses painéis subiriam ~38px e desencaixariam.
   */
  function updateAnnouncementOffset() {
    const bar = document.querySelector('.ann-bar, .utility-bar');
    const height = bar ? bar.getBoundingClientRect().height : 0;
    const scrollY = window.scrollY || window.pageYOffset;
    const visible = Math.max(0, height - scrollY);
    document.documentElement.style.setProperty('--announcement-visible-height', `${visible}px`);
  }

  /**
   * Mede a altura real do header-wrapper e atualiza --header-height no :root.
   * Só escreve se o valor for > 0.
   */
  function updateHeaderHeight() {
    const wrapper = document.querySelector('.section-header .header-wrapper');
    if (!wrapper) return;
    const h = wrapper.getBoundingClientRect().height;
    if (h > 0) {
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }

    // Calcula o offset do dropdown: distância do fundo do nav-item até o fundo do header-wrapper
    // Isso garante que o dropdown apareça logo abaixo da linha do header
    const navRow = document.querySelector('.header__nav-row');
    if (navRow && h > 0) {
      const navRowRect = navRow.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const offset = wrapperRect.bottom - navRowRect.bottom;
      if (offset >= 0) {
        document.documentElement.style.setProperty('--dropdown-offset', `${offset}px`);
      }
    }
  }

  /**
   * Atualiza a classe 'is-at-top' no header para remover a borda inferior e sombra
   * quando o usuário está no topo absoluto da página.
   */
  function updateHeaderScrollState() {
    const header = getSectionHeader();
    if (!header) return;

    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY < 10) {
      header.classList.add('is-at-top');
    } else {
      header.classList.remove('is-at-top');
    }
  }

  function watchHeaderHeight() {
    const wrapper = document.querySelector('.section-header .header-wrapper');
    if (!wrapper) return;

    // ResizeObserver: mantém --header-height atualizado quando o header muda de tamanho
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateHeaderHeight());
      ro.observe(wrapper);
    }

    // Fallbacks para garantir que os valores estão corretos
    updateHeaderHeight();
    updateAnnouncementOffset();
    requestAnimationFrame(updateHeaderHeight);
    setTimeout(updateHeaderHeight, 300);
  }

  function init() {
    const header = getSectionHeader();
    if (!header) return;

    // Inicia o watcher de altura — ResizeObserver + fallbacks
    watchHeaderHeight();

    // Inicializa o estado do scroll do header no carregamento da página
    updateHeaderScrollState();

    // ── Menu hambúrguer (mobile) ────────────────────────────────────────
    // Com o drawer aberto, o header precisa materializar em branco com
    // texto escuro (reusa o visual de .dropdown-open) — sem isso, na home
    // ele ficava transparente com ícones brancos sobre o menu branco.
    document.querySelectorAll('header-drawer details').forEach((drawerDetails) => {
      drawerDetails.addEventListener('toggle', () => {
        const drawerAberto = drawerDetails.hasAttribute('open');
        const dropdownAberto = !!header.querySelector('.header__nav-dropdown.is-open');
        header.classList.toggle('dropdown-open', drawerAberto || dropdownAberto);
      });
    });

    // Listener de scroll passivo. O encaixe do header é 100% sticky nativo —
    // aqui NÃO se mexe na posição do header. Só: alterna 'is-at-top'
    // (borda/sombra) e mantém a var de ancoragem dos painéis fixed (conta/cart).
    window.addEventListener('scroll', () => {
      updateHeaderScrollState();
      updateAnnouncementOffset();
    }, { passive: true });

    // ── Mega menu ───────────────────────────────────────────────────────
    //
    // PONTEIRO: a abertura/fechamento é 100% CSS (:hover no .header__nav-item).
    // Sem timers, sem mouseenter/mouseleave controlando visibilidade — assim o
    // menu nunca "perde" o cursor em movimentos rápidos ou diagonais. O JS abaixo
    // só sincroniza ARIA e o estado branco do header no hover de ponteiro.
    //
    // TECLADO: Enter/Espaço no link-âncora alternam o dropdown via classe
    // .is-open (sem navegar); Escape fecha e devolve o foco; sair do item via
    // Tab (foco fora) também fecha.
    document.querySelectorAll('.header__nav-item').forEach((item) => {
      const dropdown = item.querySelector('.header__nav-dropdown');
      if (!dropdown) return;

      const trigger = item.querySelector('.header__nav-link');
      const isOpen = () => dropdown.classList.contains('is-open');
      const setExpanded = (v) => {
        if (trigger) trigger.setAttribute('aria-expanded', v ? 'true' : 'false');
      };

      // Mantém o header branco enquanto QUALQUER item está em hover ou aberto.
      const syncHeaderState = () => {
        const active = header.querySelector(
          '.header__nav-item:hover, .header__nav-dropdown.is-open'
        );
        header.classList.toggle('dropdown-open', !!active);
      };

      // Abertura por teclado (persistente via .is-open).
      const openKeyboard = () => {
        dropdown.classList.add('is-open');
        header.classList.add('dropdown-open');
        setExpanded(true);
      };
      const closeKeyboard = () => {
        dropdown.classList.remove('is-open');
        setExpanded(false);
        syncHeaderState();
      };

      // Ponteiro: apenas ARIA + header branco (a visibilidade é do CSS :hover).
      item.addEventListener('mouseenter', () => {
        setExpanded(true);
        header.classList.add('dropdown-open');
      });
      item.addEventListener('mouseleave', () => {
        if (!isOpen() && !item.contains(document.activeElement)) setExpanded(false);
        // Aguarda o :hover de um item vizinho assumir antes de decidir o header.
        requestAnimationFrame(syncHeaderState);
      });

      // Teclado no gatilho: Enter/Espaço alternam; Escape fecha.
      if (trigger) {
        trigger.addEventListener('keydown', (event) => {
          const key = event.key;
          if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
            event.preventDefault();
            isOpen() ? closeKeyboard() : openKeyboard();
          } else if ((key === 'Escape' || key === 'Esc') && isOpen()) {
            event.preventDefault();
            closeKeyboard();
          }
        });
      }

      // Escape em qualquer ponto dentro do item fecha e devolve o foco.
      item.addEventListener('keydown', (event) => {
        if ((event.key === 'Escape' || event.key === 'Esc') && isOpen()) {
          event.preventDefault();
          closeKeyboard();
          if (trigger) trigger.focus();
        }
      });

      // Sair do item via Tab/Shift+Tab (foco fora do item) fecha o dropdown.
      item.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!item.contains(document.activeElement)) closeKeyboard();
        }, 0);
      });
    });

    // Produto: nunca tem banner no topo — sólido direto.
    // Coleção: NÃO retorna aqui — se houver banner com imagem
    // (.collection-hero--with-image), a detecção abaixo ativa o transparente.
    if (document.body.classList.contains('template-product')) {
      setSolid(header);
      return;
    }

    // HOME: a cliente pediu o cabeçalho FIXO e SÓLIDO, com o banner começando
    // ABAIXO do header (sem o cabeçalho sobrepor a foto). Por isso NÃO ativamos
    // o modo transparente-sobre-banner aqui — header sólido desde o topo, e a
    // compensação (padding-top: --header-height) em header-scroll.css empurra
    // o banner para baixo. Sem 'has-transparent-header' a compensação aplica.
    if (document.body.classList.contains('template-index')) {
      setSolid(header);
      return;
    }

    // Detecta o banner por seletores conhecidos. Na home, se nenhum casar,
    // usa a 1ª seção do <main> como referência — a home sempre tem o hero no
    // topo, então o header deve nascer transparente mesmo se o seletor falhar.
    const isIndex = document.body.classList.contains('template-index');
    let banner = findFirstBanner();

    if (!banner && isIndex) {
      banner =
        document.querySelector('main .shopify-section') ||
        document.querySelector('#MainContent .shopify-section') ||
        document.querySelector('.shopify-section:not(.section-header)');
    }

    if (!banner) {
      setSolid(header);
      return;
    }

    document.body.classList.add('has-transparent-header');

    if (window.scrollY < 5) {
      setTransparent(header);
    } else {
      setSolid(header);
    }

    let firstFire = true;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (firstFire && window.scrollY < 5) {
            firstFire = false;
            return;
          }
          firstFire = false;

          if (entry.isIntersecting) {
            setTransparent(header);
          } else {
            setSolid(header);
          }
        });
      },
      {
        root: null,
        rootMargin: '-64px 0px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(banner);

    window.addEventListener('resize', () => {
      updateHeaderHeight();
      updateAnnouncementOffset();
      if (window.scrollY < 5) {
        setTransparent(header);
      }
    }, { passive: true });

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
